import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Grid,
  Box,
  Divider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Skeleton, // Keep Skeleton for potential parent-level loading if desired
  Snackbar,
  Fade,
  Link,
  useTheme,
  Stack
} from '@mui/material';
import AddBalanceForm from './AddBalanceForm'; // Adjust path
import TransactionHistory from './TransactionHistory'; // Adjust path
import CurrentBalanceDisplay from './CurrentBalanceDisplay'; // Adjust path
import axiosInstance from '../../api/axiosConfig'; // Adjust path
import SchoolIcon from '@mui/icons-material/School';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HistoryIcon from '@mui/icons-material/History';
import AddCardIcon from '@mui/icons-material/AddCard';

// Logger setup
const logger = {
  info: (...args) => console.log('[INFO] AccountPage:', ...args),
  warn: (...args) => console.warn('[WARN] AccountPage:', ...args),
  error: (...args) => console.error('[ERROR] AccountPage:', ...args),
};

// --- AccountPage Component ---
function AccountPage() {
  const theme = useTheme();

  // --- State ---
  const [schools, setSchools] = useState([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState(''); // Store as string (from Select event value)
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [errorSchools, setErrorSchools] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState({ open: false, message: '', severity: 'info' });
  const [refreshKey, setRefreshKey] = useState(0); // Trigger for child component updates

  // --- Fetch Schools List ---
  const fetchSchools = useCallback(async () => {
    setLoadingSchools(true);
    setErrorSchools(null);
    setSchools([]); // Clear previous list
    logger.info('Fetching schools list...');
    try {
      // Ensure endpoint and params match your API for fetching schools
      const response = await axiosInstance.get('/api/schools'); // Use correct endpoint

      let schoolData = [];
      // Validate response based on the provided structure
      if (response.data && Array.isArray(response.data.content)) {
          schoolData = response.data.content;
      } else {
          logger.error('Unexpected schools response structure:', response.data);
          throw new Error('Unexpected data format received for schools.');
      }

      // Sort schools alphabetically by name
      schoolData.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setSchools(schoolData);
      logger.info(`Fetched ${schoolData.length} schools.`);

    } catch (err) {
      const errMsg = err.response?.data?.message || err.message || 'Failed to load schools list.';
      logger.error('Error fetching schools:', errMsg, err);
      setErrorSchools(errMsg);
      handleOpenSnackbar(errMsg, 'error'); // Use handler for snackbar
    } finally {
      setLoadingSchools(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // No dependencies needed here if handleOpenSnackbar is stable or defined outside

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]); // Fetch schools on component mount

  // --- Handle School Selection Change ---
  const handleSchoolChange = (event) => {
    const newSchoolId = event.target.value; // String value from <MenuItem>
    logger.info(`handleSchoolChange: Value selected = "${newSchoolId}" (Type: ${typeof newSchoolId})`);

    // Update state with the raw string value
    setSelectedSchoolId(newSchoolId);

    // Increment refresh key only if a valid school is selected (not the placeholder)
    if (newSchoolId && newSchoolId !== '') {
      logger.info(`handleSchoolChange: Valid school selected, incrementing refreshKey.`);
      setRefreshKey(prevKey => prevKey + 1);
    }
  };

  // --- Log state changes for debugging ---
  useEffect(() => {
    logger.info(`State updated: selectedSchoolId = "${selectedSchoolId}", refreshKey = ${refreshKey}`);
  }, [selectedSchoolId, refreshKey]);

  // --- Handle Balance Update Callback ---
  const handleBalanceUpdate = useCallback(() => {
    logger.info('handleBalanceUpdate: Callback received, incrementing refreshKey.');
    setRefreshKey(prevKey => prevKey + 1); // Increment key to trigger refetch in children
  }, []);

  // --- Snackbar Utils ---
  const handleOpenSnackbar = (message, severity = 'info') => {
    setSnackbarInfo({ open: true, message, severity });
  };
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarInfo({ ...snackbarInfo, open: false });
  };

  // --- Memoized Selected School Name ---
  const selectedSchoolName = useMemo(() => {
    if (!selectedSchoolId) return null;
    // Compare as strings since selectedSchoolId state is a string
    const school = schools.find(s => String(s.schoolId) === selectedSchoolId); // Use schoolId field from response
    return school ? school.name : `School ID: ${selectedSchoolId}`; // Fallback display
  }, [selectedSchoolId, schools]);


  // --- Render School Selector ---
const renderSchoolSelector = () => (
    <Paper elevation={0} sx={{ p: 2.5, mb: 4, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <SchoolIcon color="action"/> Select School Account
      </Typography>
      <FormControl fullWidth variant="outlined" disabled={loadingSchools}>
        <InputLabel id="school-select-label">School</InputLabel>
        <Select
          labelId="school-select-label"
          id="school-select"
          value={selectedSchoolId} // Controlled component
          onChange={handleSchoolChange}
          label="School"
        >
          <MenuItem value="" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            -- Select a School --
          </MenuItem>
          {schools.map((school) => (
            // Use schoolId field from your response structure
            // Ensure value passed to MenuItem is a string for controlled Select
            <MenuItem key={school.schoolId} value={String(school.schoolId)}>
              {school.name} (ID: {school.schoolId})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {/* Loading/Error Feedback */}
      {loadingSchools && <CircularProgress size={24} sx={{ mt: 1.5, display: 'block', mx: 'auto', color: 'text.secondary' }} />}
      {errorSchools && !loadingSchools && (
        <Alert severity="error" sx={{ mt: 2 }} variant="outlined">
          {errorSchools} - <Link component="button" onClick={fetchSchools}>Retry</Link>
        </Alert>
      )}
    </Paper>
  );

  // --- Render Detail Section ---
  const renderDetailsSection = () => {
    // Condition 1: No school selected
    if (!selectedSchoolId) {
      // Show prompt only after school list has loaded (or failed)
      if (!loadingSchools) {
        return (
          <Alert severity="info" variant="outlined" icon={false} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            Please select a school from the dropdown to manage its account.
          </Alert>
        );
      }
      return null; // Render nothing while school list is initially loading
    }

    // --- A school IS selected ---
    // **Convert selectedSchoolId (string) to number FOR PROPS**
    const numericSchoolId = Number(selectedSchoolId);

    // **Add check for NaN right after conversion**
    if (isNaN(numericSchoolId) || numericSchoolId <= 0) {
        logger.error(`Invalid numeric school ID after conversion: ${selectedSchoolId}`);
        // Clear selection or show error
        // setSelectedSchoolId(''); // Option: Reset selection
        return <Alert severity="error">Invalid School ID encountered. Please re-select.</Alert>;
    }

    logger.info(`Rendering details section for schoolId: ${numericSchoolId} with refreshKey: ${refreshKey}`);

    // Render the grid; children handle their own loading/skeletons via the key prop change
    return (
      <Fade in={true} timeout={500}>
        <Grid container spacing={3} sx={{ mt: 0 }}>
          {/* Left Column */}
          <Grid item xs={12} md={5}>
            <Stack spacing={3}>
              {/* Balance Display Card */}
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalanceWalletIcon color="action"/> Current Balance
                </Typography>
                <CurrentBalanceDisplay
                  schoolId={numericSchoolId} // Pass valid number
                  key={`balance-${selectedSchoolId}-${refreshKey}`} // Use string ID in key for consistency
                />
              </Paper>
              {/* Add Balance Form Card */}
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, bgcolor: 'background.paper' }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AddCardIcon color="action"/> Add Credits
                </Typography>
                <AddBalanceForm
                  schoolId={numericSchoolId} // Pass valid number
                  onBalanceAdded={handleBalanceUpdate}
                  key={`addform-${selectedSchoolId}-${refreshKey}`} // Use string ID in key
                />
              </Paper>
            </Stack>
          </Grid>
          {/* Right Column */}
          <Grid item xs={12} md={7}>
            {/* Transaction History Card */}
            <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
              <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HistoryIcon color="action"/> Transaction History
                </Typography>
              </Box>
              <TransactionHistory
                schoolId={numericSchoolId} // Pass valid number
                refreshTrigger={refreshKey} // Pass number key
                key={`history-${selectedSchoolId}-${refreshKey}`} // Use string ID in key
              />
            </Paper>
          </Grid>
        </Grid>
      </Fade>
    );
  };

  // --- Main Component Return ---
  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
        Support - Account Management
      </Typography>

      {renderSchoolSelector()}

      <Divider sx={{ my: 3 }}>
        <Chip
          icon={<SchoolIcon fontSize='small'/>}
          label={selectedSchoolName ? `Details for: ${selectedSchoolName}` : "Account Details"}
          variant="outlined"
          color="default"
        />
      </Divider>

      {/* Render the details section (handles its own conditional logic) */}
      {renderDetailsSection()}

      {/* Snackbar */}
      <Snackbar
        open={snackbarInfo.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Fade}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarInfo.severity} sx={{ width: '100%' }} variant="filled" elevation={6}>
          {snackbarInfo.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default AccountPage;
